<script lang="ts">
    // Types
    import type { MakeFlowAction } from '~/jet/models';
    import type { LibraryPlaylistsItems } from '@jet-app/music-classical/models/pages/library/playlist';

    // Constants
    import { PROD_ORIGIN } from '@jet-app/music-classical/utilities/seo/constants';
    import { items, type Item } from '~/constants/navigation-items';
    import {
        allPlaylistsItem,
        libraryItems,
    } from '~/constants/library-navigation-items';

    // Components
    import Logo from '~/components/structure/Logo.svelte';
    import SearchInput from './header/SearchInput.svelte';
    import HeaderAuthContent from '~/components/structure/header/HeaderAuthContent.svelte';

    // Jet
    import { getJet } from '~/jet';
    import { makeLibraryPlaylistsIntent } from '~/jet/intents';
    import { makeExternalUrlAction } from '@jet-app/music-classical/models/actions';

    // Shared Components
    import Navigation from '@amp/web-app-components/src/components/Navigation/Navigation.svelte';
    import { loggerFor } from '@amp/web-apps-logger';

    // Stores
    import { getI18n } from '~/stores/i18n';
    import { currentTab } from '~/stores/current-tab';
    import { playlists, setPlaylists } from '~/stores/playlists';

    // Utils
    import { platform } from '@amp/web-apps-utils';
    import { buildUrl } from '~/utils/url';
    import { getLocale } from '~/utils/locale';
    import { getCommerce } from '~/utils/commerce';
    import { isPremiumSubscriber as checkIsPremiumSubscriber } from '~/utils/user';

    // Icons
    import PlaylistIcon from '~assets/icons/navigation_playlist.svg';
    import ArrowIcon from '@amp/web-app-components/assets/icons/arrow.svg';

    // Consts
    const i18n = getI18n();
    const jet = getJet();
    const locale = getLocale();
    const { state: commerceState } = getCommerce();
    const log = loggerFor('<Header>');
    const isiOS = platform.isiOS();

    // Handlers
    const onItemClick = async (
        e: CustomEvent<{ opaqueData: MakeFlowAction }>,
    ) => {
        log.info('tab selected:', e.detail);
        // TODO: rdar://73165545 (Error Handling Across App)
        await jet.perform(e.detail.opaqueData(locale));
    };

    function withFixedUrls(items: Item[]): Item[] {
        return items.map((item) => ({
            ...item,
            url: buildUrl(item.url, locale, false),
        }));
    }

    const formatPlaylistItems = (item: any): any => {
        return {
            ...item,
            icon: PlaylistIcon,
            opaqueData: () => item.segue,
            url: buildUrl(
                `/library/${item.id.type}/${item.id.resourceId}`,
                locale,
                false,
                false,
            ),
        };
    };

    const fetchPlaylistItems = async () => {
        const intent = makeLibraryPlaylistsIntent({
            ...locale,
            forNavItems: true,
        });

        const playlistItemsResponse = (await jet.dispatch(
            intent,
        )) as unknown as LibraryPlaylistsItems;

        const playlistItems = playlistItemsResponse.items;
        setPlaylists(playlistItems);
    };

    $: itemsWithFixedUrls = withFixedUrls(items);
    $: personalizedItems =
        $playlists?.length > 0
            ? [
                  allPlaylistsItem,
                  ...($playlists?.map(formatPlaylistItems) || []),
              ]
            : [];
    $: isPremiumSubscriber = checkIsPremiumSubscriber(
        $commerceState?.user?.subscriberType,
    );
    $: shouldDisplayPersonalizedSidebarItems = isPremiumSubscriber;
    $: {
        if (isPremiumSubscriber) {
            fetchPlaylistItems();
        }
    }

    const openInClassical = async () => {
        const { pathname, search } = window.location;
        const url = new URL(`${pathname}${search}`, PROD_ORIGIN);
        const metrics = { data: [], custom: {} };
        await jet.perform(makeExternalUrlAction(url.href, metrics));
    };
</script>

<div class="header" data-testid="header">
    <Navigation
        items={itemsWithFixedUrls}
        translateFn={$i18n.t}
        visibilityPreferencesKey={null}
        libraryDropEnabled={false}
        {currentTab}
        on:menuItemClick={onItemClick}
        libraryItems={shouldDisplayPersonalizedSidebarItems ? libraryItems : []}
        personalizedItems={shouldDisplayPersonalizedSidebarItems
            ? personalizedItems
            : []}
        personalizedItemsHeader={$i18n.t(
            'ASE.Web.MusicClassical.NavigationItems.LibraryPlaylists.Label',
        )}
    >
        <Logo slot="logo" />
        <HeaderAuthContent slot="auth" />
        <SearchInput
            slot="search"
            {currentTab}
            on:menuItemClick={onItemClick}
        />

        <div slot="native-cta">
            {#if isiOS}
                <button
                    class="classical-button"
                    data-testid="classical-button"
                    on:click|preventDefault={openInClassical}
                >
                    {$i18n.t('ASE.Web.MusicClassical.OpenInClassical')}
                    <ArrowIcon
                        class="classical-button-action"
                        aria-hidden="true"
                    />
                </button>
            {/if}
        </div>
    </Navigation>
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'ac-sasskit/core/locale' as *;
    @use 'ac-sasskit/core/types' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use '@amp/web-shared-styles/app/core/viewports' as *;

    .header {
        grid-area: structure-header;
        grid-gap: 0;
        height: 100%;
        position: relative;
        width: 100%;

        // TODO: rdar://98856238 (JMOTW: Sidebar content overlaps banners on short viewports)
        z-index: var(--z-web-chrome);

        @media (--sidebar-visible) {
            width: unit-set($global-sidebar-ratio-to-page, vw);

            // Stacking context below UpsellBanner and AwayFromHomeBanner
            z-index: calc(var(--z-web-chrome) - 11);
        }

        @media (--sidebar-large-visible) {
            width: $global-sidebar-width-large;
        }
    }

    .classical-button {
        color: var(--systemSecondary);
        height: 35px;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        margin-bottom: 13px;
        font: var(--body-tall);
        padding-inline-start: 25px;
        padding-inline-end: 25px;

        :global(.classical-button-action) {
            fill: var(--systemSecondary);
            flex-shrink: 0;
            height: 9px;
            margin: 0 4px;
            width: 9px;

            @include rtl {
                transform: scaleX(-1);
            }
        }
    }
</style>
